// The SSH key library, from the browser's side.
//
// Two surfaces read this list — the Settings card that manages it and the
// add-worker wizard's picker — so the query key lives here rather than being
// spelled out twice. Adding a key from the wizard has to make it appear in
// Settings without a reload, and that only works if both call it the same thing.
//
// Every shape here is metadata. The private key has no route in either
// direction, which is the whole point of picking a saved key: the secret never
// enters the browser at all.

import type { SshKeyMetadata } from "@schemas";
import { useQuery } from "@tanstack/react-query";
import { api, unwrap } from "./api";

export type { SshKeyMetadata };

export const SSH_KEYS_QUERY_KEY = ["ssh-keys"] as const;

interface SshKeysResponse {
	success: boolean;
	keys: SshKeyMetadata[];
}

export function useSshKeys() {
	return useQuery({
		queryKey: SSH_KEYS_QUERY_KEY,
		queryFn: async () =>
			((await unwrap(api.api["ssh-keys"].get())) as unknown as SshKeysResponse)
				.keys,
		// A key library changes when an operator changes it, not on its own, so
		// the wizard should not re-fetch this on every step it lands on.
		staleTime: 60_000,
	});
}

/**
 * `SHA256:AbC…xyz`, the middle elided.
 *
 * A fingerprint's job in a picker is to tell two keys apart, and 43 base64
 * characters do that no better than the ends of them while costing a line wrap
 * inside a card. The full value stays available as a title.
 */
export function shortFingerprint(fingerprint: string | null): string {
	if (!fingerprint) return "fingerprint unavailable";
	const [algo, body] = fingerprint.split(":", 2);
	if (!body || body.length <= 16) return fingerprint;
	return `${algo}:${body.slice(0, 8)}…${body.slice(-6)}`;
}
